import { useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import { Menu, X } from "lucide-react";
import { BrandLockup } from "@/components/brand-lockup";
import { NAV_LINKS } from "@/components/landing/landing-data";

/**
 * Homepage-only nav. The shared MarketingHeader (used by /terms, /privacy, /contact)
 * is deliberately not reused, so this can follow the V6 layout without touching them.
 */
export function LandingNav({ loginUrl }: { loginUrl: string }) {
  const [open, setOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <header className="lp-nav sticky top-0 z-50 w-full">
      <div className="mx-auto flex h-[72px] max-w-[1360px] items-center justify-between gap-4 px-4 sm:px-8">
        <Link href="/" className="flex items-center rounded-lg" aria-label="UnfoldWin home">
          <BrandLockup />
        </Link>

        <nav aria-label="Main" className="hidden items-center gap-8 lg:flex">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.href}
              href={l.href}
              className="rounded text-[15px] font-medium text-(--lp-body) transition-colors hover:text-(--lp-ink)"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-1.5 sm:gap-4">
          <a
            href={loginUrl}
            className="hidden rounded text-[15px] font-medium text-(--lp-body) transition-colors hover:text-(--lp-ink) sm:block"
          >
            Sign In
          </a>
          <a href={loginUrl} className="lp-btn-dark min-h-11 whitespace-nowrap px-3.5 text-sm sm:min-h-12 sm:px-[22px] sm:text-base">
            Get Started
          </a>
          <button
            ref={toggleRef}
            type="button"
            className="flex h-11 w-11 items-center justify-center rounded-xl text-(--lp-ink) lg:hidden"
            aria-expanded={open}
            aria-controls="lp-mobile-menu"
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <nav
        id="lp-mobile-menu"
        aria-label="Main"
        hidden={!open}
        className="border-t border-black/5 px-4 pb-5 pt-2 sm:px-8 lg:hidden"
      >
        <ul className="flex flex-col">
          {NAV_LINKS.map((l) => (
            <li key={l.href}>
              <Link
                href={l.href}
                onClick={() => setOpen(false)}
                className="flex min-h-12 items-center rounded text-lg font-medium text-(--lp-ink)"
              >
                {l.label}
              </Link>
            </li>
          ))}
          <li className="mt-2 border-t border-black/5 pt-2 sm:hidden">
            <a href={loginUrl} className="flex min-h-12 items-center rounded text-lg font-medium text-(--lp-ink)">
              Sign In
            </a>
          </li>
        </ul>
      </nav>
    </header>
  );
}
